import { Difficulty } from '../types/lab';

export type HelpResourceSource = 'digitalocean' | 'official' | 'community';

export interface HelpResource {
  title: string;
  url: string;
  summary: string;
  source: HelpResourceSource;
}

export interface HelpEntry {
  slug: string;
  title: string;
  difficulty: Difficulty;
  shortDescription: string;
  setupNotes: string[];
  concepts: string[];
  troubleshooting: string[];
  updatedAt: string;
  maintainer: string;
  primaryResources: HelpResource[];
  additionalResources?: HelpResource[];
}

const UPDATED_AT = '2026-04-25';
const MAINTAINER = 'Docs team';

const helpEntries: HelpEntry[] = [
  {
    slug: 'linux-file-permissions-users',
    title: 'Linux File Permissions and Users',
    difficulty: Difficulty.BEGINNER,
    shortDescription: 'Practice account management, group access, sudo policy, ACLs, and SSH key hygiene.',
    setupNotes: [
      'Work on the lab VM as root or through a sudo-capable shell.',
      'Keep the lab instructions open so required usernames, groups, paths, and modes stay exact.',
      'When handling SSH access, use public keys only and leave private keys on your own device.',
    ],
    concepts: ['users and groups', 'file modes', 'ACLs', 'sudoers drop-ins', 'SSH authorized_keys'],
    troubleshooting: [
      'If a new group membership is not visible, start a fresh login shell and check with id.',
      'Use ls, stat, getfacl, and namei to inspect ownership and permission paths.',
      'Validate sudo policy with visudo before grading so a syntax error does not lock out sudo.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'useradd(8) - Linux manual page',
        url: 'https://man7.org/linux/man-pages/man8/useradd.8.html',
        summary: 'Reference for creating local Linux users and setting account defaults.',
        source: 'official',
      },
      {
        title: 'chmod(1) - Linux manual page',
        url: 'https://man7.org/linux/man-pages/man1/chmod.1.html',
        summary: 'Explains symbolic and numeric file mode changes.',
        source: 'official',
      },
      {
        title: 'sudoers(5) - Linux manual page',
        url: 'https://man7.org/linux/man-pages/man5/sudoers.5.html',
        summary: 'Authoritative syntax reference for sudo policy files and drop-in rules.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'broken-web-server-rescue',
    title: 'Broken Web Server Rescue',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Diagnose a failing nginx site by checking config, logs, ownership, and network access.',
    setupNotes: [
      'Start with the current nginx service state before editing files.',
      'Identify the active site configuration and document root before changing either one.',
      'Check both local requests on the VM and external browser access when the run exposes a public IP.',
    ],
    concepts: ['nginx server blocks', 'TLS files', 'document roots', 'firewall rules', 'service logs'],
    troubleshooting: [
      'Run nginx -t after config changes and read the exact file and line number in the output.',
      'Check error logs when HTTP status codes differ from what the instructions expect.',
      'Confirm every parent directory in the document root path can be traversed by the nginx worker user.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'NGINX Beginner Guide',
        url: 'https://nginx.org/en/docs/beginners_guide.html',
        summary: 'Official overview of nginx configuration files, server blocks, and process control.',
        source: 'official',
      },
      {
        title: 'NGINX Admin Guide: Configuring Logging',
        url: 'https://docs.nginx.com/nginx/admin-guide/monitoring/logging/',
        summary: 'Shows where access and error logs fit into nginx troubleshooting.',
        source: 'official',
      },
      {
        title: 'How To Install Nginx on Ubuntu 22.04 - DigitalOcean',
        url: 'https://www.digitalocean.com/community/tutorials/how-to-install-nginx-on-ubuntu-22-04',
        summary: 'Baseline package, firewall, and service checks for nginx on Ubuntu.',
        source: 'digitalocean',
      },
    ],
  },
  {
    slug: 'service-startup-failures',
    title: 'Service Startup Failures',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Repair systemd units by reading dependency, environment, restart, and log signals.',
    setupNotes: [
      'List failed units before editing so you know which services are part of the scenario.',
      'Inspect unit files, drop-ins, and environment files together.',
      'Reload systemd after unit file changes before restarting services.',
    ],
    concepts: ['systemd units', 'journalctl', 'dependencies', 'environment files', 'restart policies'],
    troubleshooting: [
      'Use systemctl status for the short failure reason, then journalctl for full context.',
      'If a service keeps restarting, inspect ExecStart, WorkingDirectory, users, and required ports.',
      'After changing unit files, run systemctl daemon-reload before testing again.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'systemd.service(5)',
        url: 'https://man7.org/linux/man-pages/man5/systemd.service.5.html',
        summary: 'Official service unit directive reference.',
        source: 'official',
      },
      {
        title: 'systemctl(1)',
        url: 'https://man7.org/linux/man-pages/man1/systemctl.1.html',
        summary: 'Command reference for managing units and reading service state.',
        source: 'official',
      },
      {
        title: 'journalctl(1)',
        url: 'https://man7.org/linux/man-pages/man1/journalctl.1.html',
        summary: 'Command reference for filtering and reviewing systemd journal entries.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'django-application-disaster',
    title: 'Django Application Disaster',
    difficulty: Difficulty.ADVANCED,
    shortDescription: 'Stabilize a Django deployment across settings, database access, static files, and service wiring.',
    setupNotes: [
      'Identify the app path, virtual environment, systemd service, and reverse proxy before editing.',
      'Keep DEBUG, ALLOWED_HOSTS, database settings, and static paths aligned with the lab instructions.',
      'Restart only the services affected by each change and verify logs after each restart.',
    ],
    concepts: ['Django settings', 'Gunicorn', 'PostgreSQL access', 'static assets', 'reverse proxying'],
    troubleshooting: [
      'Use manage.py check for application-level configuration problems.',
      'Read both application service logs and web server logs when a browser request fails.',
      'Confirm file ownership and execute bits if the service cannot read the project or virtualenv.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'Django Deployment Checklist',
        url: 'https://docs.djangoproject.com/en/stable/howto/deployment/checklist/',
        summary: 'Official checklist for production Django configuration.',
        source: 'official',
      },
      {
        title: 'Django Settings Reference',
        url: 'https://docs.djangoproject.com/en/stable/ref/settings/',
        summary: 'Reference for settings that affect hosts, databases, static files, and security.',
        source: 'official',
      },
      {
        title: 'Django Static Files Deployment',
        url: 'https://docs.djangoproject.com/en/stable/howto/static-files/deployment/',
        summary: 'Explains collectstatic and serving static assets in deployment.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'nextjs-fullstack',
    title: 'Next.js Full-Stack',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Bring a Next.js, Prisma, and PostgreSQL stack online with correct build and environment settings.',
    setupNotes: [
      'Separate build-time checks from runtime service checks.',
      'Review environment files before running migrations or starting the app.',
      'Check the app, database, and reverse proxy as separate layers.',
    ],
    concepts: ['Next.js deployment', 'Prisma migrations', 'environment variables', 'Node services', 'reverse proxying'],
    troubleshooting: [
      'If build output is missing, run the framework build command and read the first real error.',
      'If database calls fail, verify the connection string and migration state before changing code.',
      'If the browser cannot reach the app, check the local port first, then the proxy.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'Next.js Deploying',
        url: 'https://nextjs.org/docs/app/building-your-application/deploying',
        summary: 'Official guidance for deploying and running Next.js applications.',
        source: 'official',
      },
      {
        title: 'Prisma Migrate',
        url: 'https://www.prisma.io/docs/orm/prisma-migrate',
        summary: 'Explains migration workflow and database schema changes.',
        source: 'official',
      },
      {
        title: 'Node.js Environment Variables',
        url: 'https://nodejs.org/api/environment_variables.html',
        summary: 'Official reference for environment variable behavior in Node.js.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'wordpress-basics',
    title: 'WordPress Basics',
    difficulty: Difficulty.BEGINNER,
    shortDescription: 'Install WordPress on a Linux web stack with correct database, web, and file permissions.',
    setupNotes: [
      'Confirm which web server, PHP, and database packages the lab expects before installing extras.',
      'Keep WordPress files, database credentials, and enabled site configuration in sync.',
      'Use the browser installer only after the web stack and database are reachable.',
    ],
    concepts: ['WordPress config', 'PHP modules', 'Apache sites', 'MySQL users', 'file ownership'],
    troubleshooting: [
      'If the installer will not load, check the web server service and PHP handling first.',
      'If database setup fails, test the database credentials from the command line.',
      'If uploads or updates fail, inspect ownership and write permissions in the WordPress tree.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'How To Install WordPress',
        url: 'https://wordpress.org/documentation/article/how-to-install-wordpress/',
        summary: 'Official WordPress installation flow and wp-config.php guidance.',
        source: 'official',
      },
      {
        title: 'Hardening WordPress',
        url: 'https://wordpress.org/documentation/article/hardening-wordpress/',
        summary: 'Official security guidance for permissions, updates, and configuration.',
        source: 'official',
      },
      {
        title: 'Apache Virtual Host Documentation',
        url: 'https://httpd.apache.org/docs/2.4/vhosts/',
        summary: 'Official reference for name-based virtual host behavior.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'wordpress-database-connection-failure',
    title: 'WordPress Database Connection Failure',
    difficulty: Difficulty.BEGINNER,
    shortDescription: 'Repair a WordPress site that cannot connect to its MySQL database.',
    setupNotes: [
      'Check the WordPress config file, MySQL service, database, and user grants as separate items.',
      'Avoid changing unrelated WordPress files while troubleshooting database access.',
      'Restart services only after confirming configuration syntax and credentials.',
    ],
    concepts: ['wp-config.php', 'MySQL users', 'database grants', 'service enablement', 'socket vs TCP access'],
    troubleshooting: [
      'Test the database credentials directly with the mysql client.',
      'Check whether MySQL is active now and enabled for reboot.',
      'Read the exact WordPress error and compare it with service and credential checks.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'WordPress Database Connection Error',
        url: 'https://wordpress.org/documentation/article/faq-troubleshooting/#error-establishing-a-database-connection',
        summary: 'Official WordPress troubleshooting notes for database connection failures.',
        source: 'official',
      },
      {
        title: 'Create a New User and Grant Permissions in MySQL - DigitalOcean',
        url: 'https://www.digitalocean.com/community/tutorials/how-to-create-a-new-user-and-grant-permissions-in-mysql',
        summary: 'Practical walkthrough for creating database users and assigning privileges.',
        source: 'digitalocean',
      },
      {
        title: 'MariaDB GRANT Statement',
        url: 'https://mariadb.com/kb/en/grant/',
        summary: 'Reference for SQL grant syntax used by MySQL-compatible servers.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'memory-management-mayhem',
    title: 'Memory Management Mayhem',
    difficulty: Difficulty.ADVANCED,
    shortDescription: 'Tune memory pressure controls, swap behavior, and service stability on a stressed Linux host.',
    setupNotes: [
      'Capture baseline memory and swap state before changing kernel or service settings.',
      'Make one tuning change at a time so failed checks can be traced back quickly.',
      'Confirm persistence after reboot requirements by checking config files, not only current state.',
    ],
    concepts: ['swap files', 'sysctl', 'OOM behavior', 'systemd limits', 'memory monitoring'],
    troubleshooting: [
      'Use free, swapon, vmstat, and system logs to separate real memory pressure from config drift.',
      'If a swap check fails, inspect both active swap state and the file mode.',
      'If sysctl values reset, check whether they were written to a persistent config file.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'Linux Kernel VM Sysctl Documentation',
        url: 'https://docs.kernel.org/admin-guide/sysctl/vm.html',
        summary: 'Official reference for vm.* memory tuning parameters.',
        source: 'official',
      },
      {
        title: 'proc_sys_vm(5) - Linux manual page',
        url: 'https://man7.org/linux/man-pages/man5/proc_sys_vm.5.html',
        summary: 'Linux manual page for /proc/sys/vm controls.',
        source: 'official',
      },
      {
        title: 'How To Add Swap Space on Ubuntu 22.04 - DigitalOcean',
        url: 'https://www.digitalocean.com/community/tutorials/how-to-add-swap-space-on-ubuntu-22-04',
        summary: 'DigitalOcean walkthrough for creating and persisting a swap file.',
        source: 'digitalocean',
      },
    ],
  },
  {
    slug: 'react-node-stack-breakdown',
    title: 'React Node Stack Breakdown',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Restore a React, Node.js, database, and proxy deployment by checking each layer.',
    setupNotes: [
      'Confirm frontend build output, backend process state, database reachability, and proxy routing separately.',
      'Keep runtime environment variables out of source files unless the lab explicitly says otherwise.',
      'Use process-manager status before restarting services repeatedly.',
    ],
    concepts: ['React builds', 'Node.js services', 'PM2', 'API health checks', 'reverse proxying'],
    troubleshooting: [
      'If the page loads but API calls fail, inspect browser network errors and backend logs.',
      'If the backend exits, check package scripts, environment variables, and database connection errors.',
      'If the process manager state is not persistent, compare current process status with saved startup configuration.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'React Learn',
        url: 'https://react.dev/learn',
        summary: 'Official React learning and component model reference.',
        source: 'official',
      },
      {
        title: 'Node.js Getting Started',
        url: 'https://nodejs.org/en/learn/getting-started/introduction-to-nodejs',
        summary: 'Official introduction to running JavaScript applications with Node.js.',
        source: 'official',
      },
      {
        title: 'PM2 Quick Start',
        url: 'https://pm2.keymetrics.io/docs/usage/quick-start/',
        summary: 'Process-manager commands for starting, inspecting, and saving Node.js processes.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'database-replication',
    title: 'Database Replication',
    difficulty: Difficulty.ADVANCED,
    shortDescription: 'Repair PostgreSQL streaming replication and verify monitoring and failover readiness.',
    setupNotes: [
      'Identify the primary and replica hosts before changing PostgreSQL configuration.',
      'Separate authentication, network reachability, WAL settings, and replica state checks.',
      'Use PostgreSQL views to confirm replication state instead of relying only on service status.',
    ],
    concepts: ['streaming replication', 'WAL', 'replication slots', 'pg_hba.conf', 'replication monitoring'],
    troubleshooting: [
      'If the replica cannot connect, check pg_hba.conf, listen addresses, credentials, and network path.',
      'If replication lags, compare WAL sender and receiver state on both hosts.',
      'If a monitoring script fails, test its command manually as the same user that will run it.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'PostgreSQL High Availability and Replication',
        url: 'https://www.postgresql.org/docs/current/high-availability.html',
        summary: 'Official overview of PostgreSQL high-availability and replication options.',
        source: 'official',
      },
      {
        title: 'PostgreSQL Warm Standby and Streaming Replication',
        url: 'https://www.postgresql.org/docs/current/warm-standby.html',
        summary: 'Official guide for streaming replication concepts and configuration.',
        source: 'official',
      },
      {
        title: 'PostgreSQL Monitoring Statistics',
        url: 'https://www.postgresql.org/docs/current/monitoring-stats.html',
        summary: 'Reference for views such as pg_stat_replication and related monitoring data.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'storage-migration-scaling',
    title: 'Storage Migration and Scaling',
    difficulty: Difficulty.ADVANCED,
    shortDescription: 'Move application storage onto expandable block storage with safe mounts and monitoring.',
    setupNotes: [
      'Map current mount points, data paths, and service dependencies before moving files.',
      'Preserve ownership, permissions, and symlinks when copying application data.',
      'Confirm fstab and service behavior so the change survives reboot.',
    ],
    concepts: ['block volumes', 'filesystems', 'LVM', 'mount points', 'data migration'],
    troubleshooting: [
      'Use lsblk, findmnt, df, and mount to verify the active storage layout.',
      'If an app cannot find data after migration, check path ownership and whether the expected mount is active.',
      'If expansion checks fail, verify both the block device size and filesystem size.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'lvm(8) - Linux manual page',
        url: 'https://man7.org/linux/man-pages/man8/lvm.8.html',
        summary: 'Command reference for Linux Logical Volume Manager operations.',
        source: 'official',
      },
      {
        title: 'DigitalOcean Volumes How-To',
        url: 'https://docs.digitalocean.com/products/volumes/how-to/',
        summary: 'DigitalOcean docs for creating, attaching, formatting, and mounting volumes.',
        source: 'digitalocean',
      },
      {
        title: 'Expand Block Storage Partitions - DigitalOcean',
        url: 'https://docs.digitalocean.com/products/volumes/how-to/expand-partitions/',
        summary: 'DigitalOcean guidance for growing partitions and filesystems after volume expansion.',
        source: 'digitalocean',
      },
    ],
  },
  {
    slug: 'backup-disaster-recovery',
    title: 'Backup and Disaster Recovery',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Create, schedule, rotate, and verify database backups with realistic restore checks.',
    setupNotes: [
      'Identify what data must be backed up and where backup artifacts should be stored.',
      'Treat backup creation, retention, and restore validation as separate requirements.',
      'Run backup commands manually before adding them to scheduled automation.',
    ],
    concepts: ['pg_dump', 'restore drills', 'cron', 'retention', 'checksums'],
    troubleshooting: [
      'If a backup file is missing, test the command outside cron and inspect permissions.',
      'If cron appears inactive, check schedule syntax and system logs.',
      'If restore validation fails, confirm the backup format matches the restore command.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'PostgreSQL Backup and Restore',
        url: 'https://www.postgresql.org/docs/current/backup-dump.html',
        summary: 'Official reference for logical backups using pg_dump and pg_restore.',
        source: 'official',
      },
      {
        title: 'crontab(5) - Linux manual page',
        url: 'https://man7.org/linux/man-pages/man5/crontab.5.html',
        summary: 'Reference for cron file syntax and environment behavior.',
        source: 'official',
      },
      {
        title: 'pg_restore',
        url: 'https://www.postgresql.org/docs/current/app-pgrestore.html',
        summary: 'Official restore command reference for PostgreSQL archive backups.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'nginx-setup',
    title: 'Nginx Setup',
    difficulty: Difficulty.BEGINNER,
    shortDescription: 'Install nginx, serve static content, and make the service survive reboot.',
    setupNotes: [
      'Confirm package installation and service state before editing site files.',
      'Use the document root and content paths shown in the lab instructions.',
      'Test locally on the VM before checking from the browser.',
    ],
    concepts: ['nginx packages', 'static files', 'document roots', 'systemd enablement', 'HTTP status checks'],
    troubleshooting: [
      'If nginx will not start, run nginx -t and inspect the reported config file.',
      'If the wrong page appears, check which site is enabled and which root it serves.',
      'If reboot persistence fails, verify the service is enabled, not only active.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'NGINX Beginner Guide',
        url: 'https://nginx.org/en/docs/beginners_guide.html',
        summary: 'Official guide for basic nginx configuration and static file serving.',
        source: 'official',
      },
      {
        title: 'Serving Static Content with NGINX',
        url: 'https://docs.nginx.com/nginx/admin-guide/web-server/serving-static-content/',
        summary: 'Official guidance for document roots and static file delivery.',
        source: 'official',
      },
      {
        title: 'How To Install Nginx on Ubuntu 22.04 - DigitalOcean',
        url: 'https://www.digitalocean.com/community/tutorials/how-to-install-nginx-on-ubuntu-22-04',
        summary: 'DigitalOcean setup path for nginx package, service, and firewall checks.',
        source: 'digitalocean',
      },
    ],
  },
  {
    slug: 'basic-shell-scripting',
    title: 'Basic Shell Scripting',
    difficulty: Difficulty.BEGINNER,
    shortDescription: 'Write small Bash scripts that handle arguments, files, command output, and errors predictably.',
    setupNotes: [
      'Create scripts in the paths named by the lab instructions and make them executable when required.',
      'Run scripts from a clean shell so hidden aliases or local variables do not affect results.',
      'Test argument handling with both expected and unexpected input.',
    ],
    concepts: ['shebangs', 'variables', 'conditionals', 'exit codes', 'file permissions'],
    troubleshooting: [
      'If a script will not run, check the shebang, execute bit, and line endings.',
      'If output differs, compare whitespace, filenames, and argument order carefully.',
      'Use shellcheck-style reasoning for quoting variables and handling failed commands.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'GNU Bash Manual',
        url: 'https://www.gnu.org/software/bash/manual/bash.html',
        summary: 'Official Bash language and built-in command reference.',
        source: 'official',
      },
      {
        title: 'Bash Shell Scripts',
        url: 'https://www.gnu.org/software/bash/manual/bash.html#Shell-Scripts',
        summary: 'Official notes on script files, execution, and interpreter behavior.',
        source: 'official',
      },
      {
        title: 'ShellCheck Wiki',
        url: 'https://www.shellcheck.net/wiki/Home',
        summary: 'Community explanations for common shell scripting mistakes.',
        source: 'community',
      },
    ],
  },
  {
    slug: 'spaces-backup-automation',
    title: 'Spaces Backup Automation',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Automate encrypted backups to DigitalOcean Spaces with scheduling, retention, and verification.',
    setupNotes: [
      'Confirm the lab-provided Spaces settings before running sync or lifecycle commands.',
      'Keep credentials in the expected environment or config location, not in scripts.',
      'Test a single upload and restore path before enabling scheduled automation.',
    ],
    concepts: ['S3-compatible storage', 'backup encryption', 'retention', 'scheduled jobs', 'restore verification'],
    troubleshooting: [
      'If uploads fail, check endpoint, bucket, region, credentials, and clock skew.',
      'If scheduled backups do not appear, test the command manually as the scheduler user.',
      'If retention checks fail, inspect object names and timestamps before changing deletion logic.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'DigitalOcean Spaces Overview',
        url: 'https://docs.digitalocean.com/products/spaces/',
        summary: 'DigitalOcean product docs for Spaces concepts and capabilities.',
        source: 'digitalocean',
      },
      {
        title: 'Manage Spaces with AWS CLI',
        url: 'https://docs.digitalocean.com/products/spaces/how-to/use-aws-sdks/',
        summary: 'DigitalOcean guidance for using S3-compatible tools and SDKs with Spaces.',
        source: 'digitalocean',
      },
      {
        title: 'AWS CLI s3 sync',
        url: 'https://docs.aws.amazon.com/cli/latest/reference/s3/sync.html',
        summary: 'Command reference for sync behavior used by S3-compatible backup workflows.',
        source: 'official',
      },
    ],
  },
  {
    slug: 'k3s-mini-cluster',
    title: 'Kubernetes Mini-Cluster (k3s)',
    difficulty: Difficulty.INTERMEDIATE,
    shortDescription: 'Build a three-node K3s cluster and expose a workload through Kubernetes services.',
    setupNotes: [
      'Keep server and agent node names aligned with the lab manifest.',
      'Use the server URL and node token from the cluster, not a token from another run.',
      'Check node readiness before creating or testing workloads.',
    ],
    concepts: ['K3s server and agents', 'node tokens', 'kubectl context', 'deployments', 'NodePort services'],
    troubleshooting: [
      'If agents do not join, check K3S_URL, K3S_TOKEN, firewall reachability, and node names.',
      'If kubectl fails, confirm the kubeconfig path, permissions, and cluster endpoint.',
      'If a service is unreachable, inspect pods, endpoints, service type, and the selected node port.',
    ],
    updatedAt: UPDATED_AT,
    maintainer: MAINTAINER,
    primaryResources: [
      {
        title: 'K3s Quick-Start Guide',
        url: 'https://docs.k3s.io/quick-start',
        summary: 'Official server and agent installation flow.',
        source: 'official',
      },
      {
        title: 'K3s Agent CLI',
        url: 'https://docs.k3s.io/cli/agent',
        summary: 'Reference for agent flags and join configuration.',
        source: 'official',
      },
      {
        title: 'Kubernetes Services',
        url: 'https://kubernetes.io/docs/concepts/services-networking/service/',
        summary: 'Official explanation of service types, endpoints, and NodePort behavior.',
        source: 'official',
      },
    ],
  },
];

export function getAllHelpEntries(): HelpEntry[] {
  return helpEntries;
}

export function getHelpEntryBySlug(slug: string): HelpEntry | undefined {
  return helpEntries.find(entry => entry.slug === slug);
}

export default helpEntries;
